import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Plus, Edit2, Trash2, UserCheck, Calendar, DollarSign, Receipt } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const getAuthHeader = () => ({
  Authorization: `Bearer ${localStorage.getItem('adminToken')}`
});

const ReceptionManager = () => {
  const [activeTab, setActiveTab] = useState('managers');
  const [receptions, setReceptions] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedReception, setSelectedReception] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    mobileNumber: '',
    password: '',
    locationId: '',
    locationName: ''
  });

  useEffect(() => {
    fetchReceptions();
    fetchLocations();
  }, []);

  const fetchReceptions = async () => {
    try {
      const response = await axios.get(`${API}/api/admin/reception`, {
        headers: getAuthHeader()
      });
      setReceptions(response.data);
    } catch (error) {
      console.error('Failed to fetch receptions:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchLocations = async () => {
    try {
      const response = await axios.get(`${API}/api/locations`);
      setLocations(response.data);
    } catch (error) {
      console.error('Failed to fetch locations:', error);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();

    if (!formData.name || !formData.email || !formData.mobileNumber || !formData.password || !formData.locationId) {
      alert('Please fill all required fields');
      return;
    }

    const location = locations.find(loc => loc._id === formData.locationId);
    
    try {
      await axios.post(
        `${API}/api/admin/reception`,
        {
          ...formData,
          locationName: location?.name || ''
        },
        { headers: getAuthHeader() }
      );

      alert('Reception manager created successfully');
      setShowCreateModal(false);
      setFormData({ name: '', email: '', mobileNumber: '', password: '', locationId: '', locationName: '' });
      fetchReceptions();
    } catch (error) {
      alert('Failed to create: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleEdit = (reception) => {
    setSelectedReception(reception);
    setFormData({
      name: reception.name,
      email: reception.email,
      mobileNumber: reception.mobileNumber,
      password: '',
      locationId: reception.locationId,
      locationName: reception.locationName
    });
    setShowEditModal(true);
  };

  const handleUpdate = async (e) => {
    e.preventDefault();

    const location = locations.find(loc => loc._id === formData.locationId);
    const updateData = {
      name: formData.name,
      email: formData.email,
      mobileNumber: formData.mobileNumber,
      locationId: formData.locationId,
      locationName: location?.name || formData.locationName
    };

    if (formData.password) {
      updateData.password = formData.password;
    }

    try {
      await axios.put(
        `${API}/api/admin/reception/${selectedReception._id}`,
        updateData,
        { headers: getAuthHeader() }
      );

      alert('Reception manager updated successfully');
      setShowEditModal(false);
      setSelectedReception(null);
      setFormData({ name: '', email: '', mobileNumber: '', password: '', locationId: '', locationName: '' });
      fetchReceptions();
    } catch (error) {
      alert('Failed to update: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this reception manager?')) return;

    try {
      await axios.delete(`${API}/api/admin/reception/${id}`, {
        headers: getAuthHeader()
      });
      alert('Reception manager deleted successfully');
      fetchReceptions();
    } catch (error) {
      alert('Failed to delete: ' + (error.response?.data?.error || error.message));
    }
  };

  if (loading) {
    return <div className="text-white text-center py-12">Loading...</div>;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-black text-white mb-2">Reception Management</h1>
          <p className="text-gray-400">Manage reception managers and daily operations</p>
        </div>
        {activeTab === 'managers' && (
          <button
            onClick={() => setShowCreateModal(true)}
            className="bg-yellow-400 hover:bg-yellow-300 text-black px-6 py-3 rounded-lg font-bold flex items-center space-x-2 transition-colors"
          >
            <Plus size={20} />
            <span>Add Reception Manager</span>
          </button>
        )}
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-700">
        <div className="flex space-x-8">
          <button
            onClick={() => setActiveTab('managers')}
            className={`pb-4 px-1 border-b-2 font-medium transition-colors flex items-center space-x-2 ${
              activeTab === 'managers'
                ? 'border-yellow-400 text-yellow-400'
                : 'border-transparent text-gray-400 hover:text-white'
            }`}
          >
            <UserCheck size={18} />
            <span>Reception Managers</span>
          </button>
          <button
            onClick={() => setActiveTab('daily-sales')}
            className={`pb-4 px-1 border-b-2 font-medium transition-colors flex items-center space-x-2 ${
              activeTab === 'daily-sales'
                ? 'border-yellow-400 text-yellow-400'
                : 'border-transparent text-gray-400 hover:text-white'
            }`}
          >
            <DollarSign size={18} />
            <span>Daily Sales</span>
          </button>
          <button
            onClick={() => setActiveTab('daily-expense')}
            className={`pb-4 px-1 border-b-2 font-medium transition-colors flex items-center space-x-2 ${
              activeTab === 'daily-expense'
                ? 'border-yellow-400 text-yellow-400'
                : 'border-transparent text-gray-400 hover:text-white'
            }`}
          >
            <Receipt size={18} />
            <span>Daily Expense</span>
          </button>
        </div>
      </div>

      {/* Tab Content */}
      {activeTab === 'managers' && (
        <ReceptionManagersTab
          receptions={receptions}
          onEdit={handleEdit}
          onDelete={handleDelete}
        />
      )}

      {activeTab === 'daily-sales' && (
        <DailySalesTab locations={locations} />
      )}

      {activeTab === 'daily-expense' && (
        <DailyExpenseTab locations={locations} />
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <ReceptionFormModal
          title="Create Reception Manager"
          formData={formData}
          setFormData={setFormData}
          locations={locations}
          onSubmit={handleCreate}
          onClose={() => {
            setShowCreateModal(false);
            setFormData({ name: '', email: '', mobileNumber: '', password: '', locationId: '', locationName: '' });
          }}
        />
      )}

      {/* Edit Modal */}
      {showEditModal && (
        <ReceptionFormModal
          title="Edit Reception Manager"
          formData={formData}
          setFormData={setFormData}
          locations={locations}
          onSubmit={handleUpdate}
          onClose={() => {
            setShowEditModal(false);
            setSelectedReception(null);
            setFormData({ name: '', email: '', mobileNumber: '', password: '', locationId: '', locationName: '' });
          }}
          isEdit={true}
        />
      )}
    </div>
  );
};

// Reception Managers Tab Component
const ReceptionManagersTab = ({ receptions, onEdit, onDelete }) => {
  return (
    <div className="bg-gray-800 border border-gray-700 rounded-xl overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-900">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Name</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Email</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Mobile Number</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Location</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Status</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Last Login</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-700">
            {receptions.map((reception) => (
              <tr key={reception._id} className="hover:bg-gray-700/50">
                <td className="px-4 py-4 text-white">{reception.name}</td>
                <td className="px-4 py-4 text-white text-sm">{reception.email}</td>
                <td className="px-4 py-4 text-white">{reception.mobileNumber}</td>
                <td className="px-4 py-4 text-white">{reception.locationName}</td>
                <td className="px-4 py-4">
                  <span
                    className={`px-2 py-1 rounded-full text-xs font-bold ${
                      reception.isActive
                        ? 'bg-green-500/20 text-green-400'
                        : 'bg-red-500/20 text-red-400'
                    }`}
                  >
                    {reception.isActive ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td className="px-4 py-4 text-white text-sm">
                  {reception.lastLogin
                    ? new Date(reception.lastLogin).toLocaleString()
                    : 'Never'}
                </td>
                <td className="px-4 py-4">
                  <div className="flex space-x-2">
                    <button
                      onClick={() => onEdit(reception)}
                      className="p-2 hover:bg-blue-500/20 rounded-lg text-blue-400 transition-colors"
                      title="Edit"
                    >
                      <Edit2 size={16} />
                    </button>
                    <button
                      onClick={() => onDelete(reception._id)}
                      className="p-2 hover:bg-red-500/20 rounded-lg text-red-400 transition-colors"
                      title="Delete"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {receptions.length === 0 && (
        <div className="text-center py-12">
          <UserCheck className="mx-auto mb-4 text-gray-600" size={48} />
          <p className="text-gray-400">No reception managers found</p>
        </div>
      )}
    </div>
  );
};

// Reception Form Modal Component
const ReceptionFormModal = ({ title, formData, setFormData, locations, onSubmit, onClose, isEdit }) => {
  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-800 rounded-2xl max-w-xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <h2 className="text-2xl font-black text-white mb-6">{title}</h2>

          <form onSubmit={onSubmit} className="space-y-4">
            <div>
              <label className="text-white font-medium mb-2 block">Name *</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-yellow-400"
                placeholder="Enter name"
                required
              />
            </div>

            <div>
              <label className="text-white font-medium mb-2 block">Email *</label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-yellow-400"
                placeholder="Enter email address"
                required
              />
            </div>

            <div>
              <label className="text-white font-medium mb-2 block">Mobile Number *</label>
              <input
                type="tel"
                value={formData.mobileNumber}
                onChange={(e) => setFormData({ ...formData, mobileNumber: e.target.value })}
                className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-yellow-400"
                placeholder="10-digit mobile number"
                pattern="[0-9]{10}"
                required
              />
              <p className="text-sm text-gray-400 mt-1">This will be used as login ID</p>
            </div>

            <div>
              <label className="text-white font-medium mb-2 block">
                Password {isEdit && '(Leave empty to keep current)'}
              </label>
              <input
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-yellow-400"
                placeholder="Enter password"
                required={!isEdit}
              />
            </div>

            <div>
              <label className="text-white font-medium mb-2 block">Location *</label>
              <select
                value={formData.locationId}
                onChange={(e) => setFormData({ ...formData, locationId: e.target.value })}
                className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-yellow-400"
                required
              >
                <option value="">Select Location</option>
                {locations.map((loc) => (
                  <option key={loc._id} value={loc._id}>
                    {loc.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex justify-end space-x-4 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="px-6 py-3 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-6 py-3 bg-yellow-400 hover:bg-yellow-300 text-black font-bold rounded-lg transition-colors"
              >
                {isEdit ? 'Update' : 'Create'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

// Placeholder for Daily Sales Tab (will implement detailed component)
const DailySalesTab = ({ locations }) => {
  return (
    <div className="bg-gray-800 border border-gray-700 rounded-xl p-8 text-center">
      <DollarSign className="mx-auto mb-4 text-yellow-400" size={48} />
      <p className="text-white text-lg mb-2">Daily Sales Management</p>
      <p className="text-gray-400">Coming soon - Track and manage daily sales entries</p>
    </div>
  );
};

// Placeholder for Daily Expense Tab (will implement detailed component)
const DailyExpenseTab = ({ locations }) => {
  return (
    <div className="bg-gray-800 border border-gray-700 rounded-xl p-8 text-center">
      <Receipt className="mx-auto mb-4 text-yellow-400" size={48} />
      <p className="text-white text-lg mb-2">Daily Expense Management</p>
      <p className="text-gray-400">Coming soon - Track and manage daily expense entries</p>
    </div>
  );
};

export default ReceptionManager;
